import React, { useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlightIcon from '@mui/icons-material/Flight';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Day, Stop } from '../../types';
import { Board, DayLayover, TownGroup, airborne, dayCapacity, dayJourney, dayLoad, formatDay, townGroups } from '../../lib/plan';
import { formatHours } from '../../lib/booking';
import PlaceCard, { DRAG_TYPE, LegCard, MoveTarget } from './PlaceCard';
import InlineEdit from './InlineEdit';
import { sameTown } from '../../lib/towns';

const LINE = '#D9D0C4';

interface Props {
  board: Board;
  targets: MoveTarget[];
  onPlace: (stopId: string, dayId: string | null, beforeId: string | null) => void;
  onKeep: (stop: Stop) => void;
  onReject: (stop: Stop) => void;
  onToggleMustSee: (stop: Stop) => void;
  onRemove: (stop: Stop) => void;
  onOpenLeg: (stop: Stop) => void;
  /** Edit where you sleep: a whole town's nights, or one night. */
  onEditNights: (days: Day[]) => void;
  /** Name a day ("Bund and the old town"); null clears it. */
  onLabelDay: (day: Day, label: string | null) => void;
  /** Research what fits a layover: food, a lounge, a short walk. */
  onResearchLayover: (layover: DayLayover) => void;
}

/**
 * The plan as a vertical tree: the town you sleep in, its days, each day's
 * places. Reads top to bottom like an itinerary and fits beside the candidates
 * panel at any width, where day columns did not. Each day collapses to a
 * one-line summary.
 */
export default function TripTree(props: Props) {
  const { board } = props;
  const groups = townGroups(board.trip, board.days);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (dayId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });

  if (board.days.length === 0) {
    return (
      <Box sx={{ p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <Typography color="text.secondary">Add start and finish dates in Trip basics and the days appear here.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button size="small" variant="outlined" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button size="small" variant="outlined" onClick={() => setCollapsed(new Set(board.days.map((d) => d.day_id)))}>
          Collapse all
        </Button>
      </Box>
      <Box component="ol" aria-label="Trip by town and day" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {groups.map((g, gi) => (
          <TownBranch
            key={g.days[0].day_id}
            group={g}
            first={gi === 0}
            dayIndex={(d) => board.days.indexOf(d)}
            collapsed={collapsed}
            onToggle={toggle}
            {...props}
          />
        ))}
      </Box>
    </Box>
  );
}

function TownBranch({
  group,
  first,
  dayIndex,
  collapsed,
  onToggle,
  ...props
}: Props & {
  group: TownGroup;
  first: boolean;
  dayIndex: (d: Day) => number;
  collapsed: Set<string>;
  onToggle: (dayId: string) => void;
}) {
  const { trip } = props.board;
  const lodgings = Array.from(new Set(group.days.filter((d) => d.date !== trip.end_date).map((d) => d.lodging).filter(Boolean)));
  const fixed = !group.transit && group.days.some((d) => d.base_fixed);
  const firstDate = group.days[0].date;
  const lastNight = group.days.filter((d) => d.date !== trip.end_date).slice(-1)[0]?.date ?? firstDate;

  return (
    <Box component="li" sx={{ mt: first ? 0 : 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={() => props.onEditNights(group.days.filter((d) => d.date !== trip.end_date))}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 0.25,
          width: '100%',
          p: 1.5,
          textAlign: 'left',
          font: 'inherit',
          color: 'text.primary',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          '&:hover': { borderColor: 'primary.light' },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: group.town ? 'primary.main' : group.transit ? '#EEE7DD' : 'transparent',
            border: group.town || group.transit ? 'none' : '1.5px dashed',
            borderColor: 'text.disabled',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          {group.transit ? (
            <FlightIcon sx={{ fontSize: 17, color: 'text.secondary', transform: 'rotate(45deg)' }} />
          ) : (
            <HotelOutlinedIcon sx={{ fontSize: 17, color: group.town ? '#FFFFFF' : 'text.disabled' }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: group.town ? 'primary.main' : 'text.secondary' }}>
            {group.transit ? 'Travelling' : group.town ?? 'Where do you sleep?'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {group.nights
              ? `${group.nights} ${group.nights === 1 ? 'night' : 'nights'} · ${formatDay(firstDate)}${
                  lastNight !== firstDate ? ` – ${formatDay(lastNight)}` : ''
                }`
              : 'Finish · no night'}
          </Typography>
          {fixed ? (
            <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'secondary.dark', fontWeight: 600 }}>
              <PushPinIcon sx={{ fontSize: 13 }} /> fixed
            </Typography>
          ) : null}
        </Box>
        {group.nights ? (
          <Typography
            variant="body2"
            sx={{
              color: lodgings.length || group.transit ? 'text.secondary' : 'secondary.dark',
              flex: 'none',
              // On a phone it goes under the town, in line with the name, so the name keeps the width.
              width: { xs: '100%', sm: 'auto' },
              pl: { xs: '44px', sm: 0 },
            }}
          >
            {lodgings.length ? lodgings.join(' · ') : group.transit ? 'On board' : 'Where are you staying?'}
          </Typography>
        ) : null}
      </Box>

      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          m: 0,
          // The tree line costs a quarter of a phone's width; there the town header alone groups its days.
          ml: { xs: 0, sm: '31px' },
          pl: { xs: 0, sm: '14px' },
          pt: 0.5,
          pb: 1,
          borderLeft: { xs: 'none', sm: `2px solid ${LINE}` },
        }}
      >
        {group.days.map((d) => (
          <DayBranch key={d.day_id} day={d} index={dayIndex(d)} collapsed={collapsed.has(d.day_id)} onToggle={() => onToggle(d.day_id)} {...props} />
        ))}
      </Box>
    </Box>
  );
}

function DayBranch({
  day,
  index,
  collapsed,
  onToggle,
  board,
  targets,
  onPlace,
  onKeep,
  onReject,
  onToggleMustSee,
  onRemove,
  onOpenLeg,
  onEditNights,
  onResearchLayover,
  onLabelDay,
}: Props & { day: Day; index: number; collapsed: boolean; onToggle: () => void }) {
  const [over, setOver] = useState(false);
  const { trip } = board;
  const placed = board.stops.filter((s) => s.day_id === day.day_id);
  const legs = placed.filter((s) => s.stop_type === 'transit');
  const journey = dayJourney(board, day);
  const layovers = journey.flatMap((j) => (j.kind === 'layover' ? [j.layover] : []));
  // A place in a layover's town shows inside that layover, not after the journey.
  const layoverOf = (s: Stop) => layovers.find((l) => sameTown(s.city, l.place));
  const places = placed.filter((s) => s.stop_type !== 'transit' && !layoverOf(s));
  const suggested = board.stops.filter((s) => !s.day_id && s.suggested_day_id === day.day_id);
  const load = dayLoad(board, day);
  const cap = dayCapacity(board, day);
  const inAir = airborne(board, day);
  const pct = cap ? Math.min(100, Math.round((load / cap) * 100)) : 0;
  const prev = board.days[index - 1];
  const moving = !!prev && !!day.base_city && !!prev.base_city && prev.base_city !== day.base_city;

  const tag =
    day.date === trip.start_date
      ? 'Start'
      : day.date === trip.end_date
        ? 'Finish'
        : inAir
          ? 'In the air'
          : moving || legs.length || layovers.length
            ? 'Travel day'
            : null;
  const names = [...places, ...suggested].map((s) => s.title);
  const outside = (trip.start_date && day.date < trip.start_date) || (trip.end_date && day.date > trip.end_date);

  return (
    <Box
      component="li"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DRAG_TYPE)) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData(DRAG_TYPE);
        if (id) onPlace(id, day.day_id, null);
      }}
      sx={{
        position: 'relative',
        mt: 1.25,
        // The branch from the town's line into this day.
        '&::before': {
          content: '""',
          display: { xs: 'none', sm: 'block' },
          position: 'absolute',
          left: '-14px',
          top: 22,
          width: 10,
          borderTop: `2px solid ${LINE}`,
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          border: '1px solid',
          borderColor: over ? 'secondary.main' : outside ? 'warning.main' : 'divider',
          borderStyle: outside ? 'dashed' : 'solid',
          borderRadius: 2,
          bgcolor: over ? '#FBF1EC' : '#FBF9F6',
          transition: 'border-color 120ms, background-color 120ms',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.25, rowGap: 0.5 }}>
          <Box
            component="button"
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 36,
              ml: -1,
              px: 0.75,
              border: 0,
              borderRadius: 1,
              bgcolor: 'transparent',
              font: 'inherit',
              color: 'text.primary',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              Day {index + 1}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {formatDay(day.date)}
          </Typography>
          {tag ? (
            <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 600 }}>
              {tag}
            </Typography>
          ) : null}
          {outside ? (
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
              Outside your dates
            </Typography>
          ) : null}
          {cap === 0 && load === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Travelling
            </Typography>
          ) : (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {Math.round(load * 10) / 10} of {cap} h
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct >= 95 ? 'secondary' : 'primary'}
              sx={{ width: 64, height: 6, borderRadius: 3, bgcolor: '#EEE7DD' }}
              aria-label={`Day ${index + 1} is ${pct}% full`}
            />
          </Box>
          )}
        </Box>

        <Box sx={{ ml: { xs: 0.5, sm: 3.5 }, mt: 0.25 }}>
          <InlineEdit
            value={day.label}
            label={`Label for Day ${index + 1}`}
            placeholder="Name this day"
            maxLength={80}
            textSx={{ fontSize: '0.95rem', fontWeight: 600, color: 'primary.main' }}
            onSave={(label) => onLabelDay(day, label)}
          />
        </Box>

        {collapsed ? (
          <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0.5, sm: 3.5 }, mt: 0.5 }}>
            {names.length} {names.length === 1 ? 'place' : 'places'}
            {legs.some((l) => !l.leg_mode) ? ' · 1 open choice' : ''}
            {names.length ? ` — ${names.slice(0, 3).join(', ')}${names.length > 3 ? '…' : ''}` : ''}
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 1,
              ml: { xs: 0, sm: 0.75 },
              pl: { xs: 0, sm: 1.25 },
              borderLeft: { xs: 'none', sm: '2px dashed #E4DCD1' },
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {journey.map((j) =>
              j.kind === 'leg' ? (
                <LegCard key={j.stop.stop_id} stop={j.stop} onOpen={onOpenLeg} dayDate={day.date} />
              ) : (
                <LayoverCard key={`lay-${j.layover.outbound.stop_id}`} layover={j.layover} onResearch={onResearchLayover}>
                  {placed
                    .filter((s) => s.stop_type !== 'transit' && layoverOf(s) === j.layover)
                    .map((s) => (
                      <PlaceCard
                        key={s.stop_id}
                        stop={s}
                        variant="placed"
                        targets={targets}
                        onMove={(id, dayId) => onPlace(id, dayId, null)}
                        onDropBefore={(id) => onPlace(id, day.day_id, s.stop_id)}
                        onToggleMustSee={onToggleMustSee}
                        onRemove={onRemove}
                      />
                    ))}
                </LayoverCard>
              ),
            )}
            {places.map((s) => (
              <PlaceCard
                key={s.stop_id}
                stop={s}
                variant="placed"
                targets={targets}
                onMove={(id, dayId) => onPlace(id, dayId, null)}
                onDropBefore={(id) => onPlace(id, day.day_id, s.stop_id)}
                onToggleMustSee={onToggleMustSee}
                onRemove={onRemove}
              />
            ))}
            {suggested.map((s) => (
              <PlaceCard
                key={s.stop_id}
                stop={s}
                variant="suggested"
                targets={targets}
                onMove={(id, dayId) => onPlace(id, dayId, null)}
                onKeep={onKeep}
                onReject={onReject}
                onToggleMustSee={onToggleMustSee}
                onRemove={onRemove}
              />
            ))}
            <Box
              sx={{
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px dashed',
                borderColor: over ? 'primary.main' : 'divider',
                borderRadius: 1.5,
                color: over ? 'primary.main' : 'text.disabled',
                fontSize: '0.8rem',
                fontWeight: over ? 600 : 400,
              }}
            >
              {over && load >= cap ? 'Drop here · this day will run long' : 'Drop a candidate here'}
            </Box>
            {day.date !== trip.end_date && !day.in_transit ? (
              <Button size="small" sx={{ alignSelf: 'flex-start', color: 'text.secondary' }} onClick={() => onEditNights([day])}>
                Sleep somewhere else this night
              </Button>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Time between two legs at the same place. Short as it is, it is part of the
 * trip — a meal, a lounge — so it holds that town's places and can be
 * researched on its own.
 */
function LayoverCard({
  layover,
  onResearch,
  children,
}: {
  layover: DayLayover;
  onResearch: (l: DayLayover) => void;
  children: React.ReactNode;
}) {
  const hasPlaces = React.Children.count(children) > 0;
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#F3EEE6', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {layover.place}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            layover.hours !== null ? `${formatHours(layover.hours)} layover` : 'Layover',
            layover.from && layover.until ? `${layover.from} – ${layover.until}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        <Button
          size="small"
          startIcon={<TravelExploreIcon />}
          onClick={() => onResearch(layover)}
          sx={{ ml: 'auto', minHeight: 32 }}
        >
          {hasPlaces ? 'More ideas' : 'Ideas for this layover'}
        </Button>
      </Box>
      {children}
    </Box>
  );
}
